// Shared colour palette and constants - imported by extracted components.
// TypeScript source — constants.js is a thin re-export stub for existing
// `.js` import paths. Import from constants.ts (or extensionless) in new files.

// SINGLE SOURCE OF TRUTH for the colour palette is the Tailwind `@theme` block in
// src/index.css (the `--color-zephyr-*` custom properties). This `C` object only
// REFERENCES those CSS variables, so inline `style={{…}}` colours and Tailwind
// `className` colours (bg-zephyr-teal, …) can never drift — edit a colour in ONE
// place (index.css) and both styling systems follow. (architecture-review 3.5)
// CSS variables resolve in any DOM/inline-style context; do NOT feed these into a
// non-CSS sink (jsPDF/canvas/<meta theme-color>) — use a literal hex there.
export const C = {
  teal: "var(--color-zephyr-teal)",
  tealDark: "var(--color-zephyr-teal-dark)",
  tealLight: "var(--color-zephyr-teal-light)",
  tealBg: "var(--color-zephyr-teal-bg)",
  gold: "var(--color-zephyr-gold)",
  goldLight: "var(--color-zephyr-gold-light)",
  orange: "var(--color-zephyr-orange)",
  green: "var(--color-zephyr-green)",
  greenLight: "var(--color-zephyr-green-light)",
  blue: "var(--color-zephyr-blue)",
  blueLight: "var(--color-zephyr-blue-light)",
  red: "var(--color-zephyr-red)",
  redLight: "var(--color-zephyr-red-light)",
  white: "#FFFFFF", // plain white — not a themed brand colour
  offWhite: "var(--color-zephyr-off-white)",
  border: "var(--color-zephyr-border)",
  text: "var(--color-zephyr-text)",
  muted: "var(--color-zephyr-muted)",
};

// Unified category-panel scheme (John, 5 July 2026) — wizard section headers
// (School Costs & Stationery / Uniforms / Shoes / Device), the read-view category
// panels (ChildItemReadView, DraftChildDisplay) and the Print Authorisation button
// all draw from these three tokens. Edit the colours in src/index.css @theme.
// Same caveat as `C`: CSS variables — never feed into jsPDF/canvas.
export const PANEL = {
  bg: "var(--color-zephyr-panel-bg)",
  border: "var(--color-zephyr-panel-border)",
  heading: "var(--color-zephyr-panel-heading)",
};

// Comment-thread tint by the AUTHOR'S TIER (John, 30 Jul 2026). Each new note stores
// a `tier`; legacy notes without one fall back via `byRole` (shelter → cream, any
// other → responder teal). Used by CommentPanel + DraftCommentsDisplay so the two
// comment views can never drift.
export interface CommentTierColour {
  bg: string;
  name: string;
  border: string;
}
export const COMMENT_TIER_COLOURS: Record<string, CommentTierColour> = {
  responder: { bg: "#E0F2F1", name: "#00695C", border: "#00897B40" },
  supervisor: { bg: "#E3F2FD", name: "#1565C0", border: "#90CAF9" },
  shelter: { bg: "#FFF8E1", name: "#8D6E00", border: "#FFE082" },
};

// The colour scheme for a note, from its stored tier (falling back by byRole).
export function commentTierColours(
  note: { tier?: string | null; byRole?: string | null } | null | undefined
): CommentTierColour {
  const tier = note?.tier || (note?.byRole === "shelter" ? "shelter" : "responder");
  return COMMENT_TIER_COLOURS[tier] || COMMENT_TIER_COLOURS.responder;
}

// The tier to STAMP on a new note. shelter → "shelter"; a supervisor (admin/superuser)
// → "supervisor"; every other internal user (staff/combined/picker) → "responder".
export function authorCommentTier(isStaff: boolean, isSupervisor: boolean): string {
  if (!isStaff) return "shelter";
  return isSupervisor ? "supervisor" : "responder";
}
